import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { Stack, Card, Button, Skeleton, SvgIcon, Typography, Tooltip } from '@mui/material';
import Plus from '@untitled-ui/icons-react/build/esm/Plus';
import { getMultipleMDAAssessment } from 'src/redux/actions/therapyPlanning';
import MdsAssessmentTable from './mds-assessment-table';
import { paths } from 'src/paths';
import DeletePopupModal from 'src/components/common-modals/delete-modal';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import {
  deleteSingleMdsAssessment,
  resetDeleteSingleMdsAssessment,
  getPatientDateOfService,
} from 'src/redux/actions/therapyPlanning';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { getSinglePatient } from 'src/redux/actions/patientAction';
import { Permissions, usePermission } from 'src/rbac';

const { patients, patientDetails: patientDetailsPath, mdsAssessments, createMdsAssessment } = paths;

type Props = {};

function MdsAssessments({}: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get patientId from location state (when coming from patient list)
  // or from Redux (when coming from therapy planning)
  const patientIdFromState = location.state?.patientId;
  const patientInfoFromRedux = useSelector(
    (state: any) => state?.selectPatientReducer?.patientInfo
  );
  const patientDataFromSinglePatient = useSelector(
    (state: any) => state?.getSinglePatientReducer?.patient
  );

  // Determine the patient ID to use - priority: location state > single patient reducer > select patient reducer
  const patientId =
    patientIdFromState || patientDataFromSinglePatient?.uuid || patientInfoFromRedux?.uuid;
  const patientInfo = patientDataFromSinglePatient || patientInfoFromRedux;

  const getMultipleMdsAssessment = useSelector((state: any) => state.getMultipleMDSAssessment);
  const deleteMdsAssessmentResponse = useSelector(
    (state: any) => state.deleteSingleMdsAssessmentReducer
  );
  const episodeOfCareData = useSelector((state: any) => state?.getEpisodeOfCare?.response);
  const [deleteMds, setDeleteMds] = useState<any>(null);
  const [deleteMdsAssessment, setDeleteMdsAssessment] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [message, setMessage] = useState('');
  const doesPatientHaveOpenEOC = getMultipleMdsAssessment.response
    ? getMultipleMdsAssessment.response?.data.length > 0
    : false;

  const allMdsAssessments = useMemo(() => {
    if (!getMultipleMdsAssessment?.response?.data?.length) {
      return [];
    }
    const flattenedAssessments: any[] = [];

    // Iterate through all EOCs (both OPENED and CLOSED)
    getMultipleMdsAssessment.response.data.forEach((eoc: any) => {
      if (eoc?.mdsAssessments && Array.isArray(eoc.mdsAssessments)) {
        eoc.mdsAssessments.forEach((assessment: any) => {
          flattenedAssessments.push({
            ...assessment,
            episodeOfCareUuid: eoc?.uuid,
            episodeTitle: eoc?.episodeTitle,
          });
        });
      }
    });

    // Sort by assessment date (most recent first)
    return flattenedAssessments.sort((a, b) => {
      const dateA = a?.isFinalMds ? a?.finalMds?.assessmentDate : a?.assessmentDate;
      const dateB = b?.isFinalMds ? b?.finalMds?.assessmentDate : b?.assessmentDate;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [getMultipleMdsAssessment]);

  // Fetch patient data if we have patientId from location state
  useEffect(() => {
    if (patientIdFromState && !patientDataFromSinglePatient) {
      dispatch(
        getSinglePatient({
          patientId: patientIdFromState,
        })
      );
    }
  }, [patientIdFromState, patientDataFromSinglePatient, dispatch]);

  // Fetch MDS assessments when patient ID is available
  useEffect(() => {
    if (patientId) {
      dispatch(
        getMultipleMDAAssessment({
          patientId: patientId,
        })
      );
    }
  }, [patientId, dispatch]);

  // Handle delete confirmation
  const handleDeleteMds = () => {
    if (deleteMds?.uuid) {
      dispatch(
        deleteSingleMdsAssessment({
          mdsId: deleteMds?.uuid,
        })
      );
      setDeleteMdsAssessment(false);
      setDeleteMds(null);
    }
  };

  // Handle delete button click
  const handleDeleteMdsClick = (mds: any) => {
    setDeleteMds(mds);
    setDeleteMdsAssessment(true);
  };

  // Handle edit button click
  const handleEditMds = (mds: any) => {
    navigate(`${patients}/${patientDetailsPath}${mdsAssessments}${createMdsAssessment}`, {
      state: {
        action: 'Edit',
        selectedPatient: {
          ...(patientInfo || location.state?.patientData),
          eocStartDate: episodeOfCareData?.content[0]?.startDate,
          planTracking: episodeOfCareData?.content[0]?.payerPlanEntity?.planTracking,
        },
        from: 'PATIENT_DETAILS',
        assessmentDetails: mds,
      },
    });
  };

  // Handle new assessment button click
  const handleNewAssessment = () => {
    navigate(`${patients}/${patientDetailsPath}${mdsAssessments}${createMdsAssessment}`, {
      state: {
        action: 'Create',
        selectedPatient: {
          ...(patientInfo || location.state?.patientData),
          eocStartDate: episodeOfCareData?.content[0]?.startDate,
          planTracking: episodeOfCareData?.content[0]?.payerPlanEntity?.planTracking,
        },
        from: 'PATIENT_DETAILS',
      },
    });
  };

  // Handle delete response
  useEffect(() => {
    if (
      deleteMdsAssessmentResponse?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentResponse?.response === null
    ) {
      return;
    }

    if (
      deleteMdsAssessmentResponse?.isMdsAssessmentDeleted === false &&
      deleteMdsAssessmentResponse !== null
    ) {
      setMessage(deleteMdsAssessmentResponse?.response?.message || 'Failed to delete assessment');
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(resetDeleteSingleMdsAssessment());
        setMessage('');
      }, 3000);
      return;
    } else if (deleteMdsAssessmentResponse?.isMdsAssessmentDeleted === true) {
      // Refresh data after successful delete
      if (patientId) {
        dispatch(
          getPatientDateOfService({
            patientId: patientId,
          })
        );
        dispatch(
          getMultipleMDAAssessment({
            patientId: patientId,
          })
        );
      }
      setMessage(
        deleteMdsAssessmentResponse?.response?.message || 'Assessment deleted successfully'
      );
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setMessage('');
        setIsSnackBarOpen(false);
        dispatch(resetDeleteSingleMdsAssessment());
      }, 3000);
    }
  }, [deleteMdsAssessmentResponse, patientInfo, dispatch]);

  const isLoading = getMultipleMdsAssessment?.loading;
  const totalAssessments = allMdsAssessments.length;
  const hasUpdatePatientPermission = usePermission(Permissions.UPDATE_PATIENT);

  const getTooltipMessage = useMemo(() => {
    if (!doesPatientHaveOpenEOC && !hasUpdatePatientPermission) {
      return 'An episode is required to create an MDS assessment and you do not have permission to update the patient details';
    }
    if (!doesPatientHaveOpenEOC) {
      return 'An episode is required to create an MDS assessment';
    }
    if (!hasUpdatePatientPermission) {
      return 'You do not have permission to update the patient details';
    }
    return '';
  }, [doesPatientHaveOpenEOC, hasUpdatePatientPermission]);

  return (
    <Card sx={{ padding: '1.5rem' }}>
      <Stack>
        {!isLoading && (
          <>
            <Stack
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
            >
              <Stack
                gap="1rem"
                alignItems="center"
                flexDirection="row"
                sx={{ marginBottom: '1rem' }}
              >
                <h3 style={{ margin: '0px' }}>MDS Assessments</h3>
                {totalAssessments !== 0 && (
                  <Stack
                    flexDirection="row"
                    sx={{
                      borderRadius: '12px',
                      display: 'flex',
                      border: '1px solid #00000042',
                      color: '#111927',
                      padding: '3px 10px',
                    }}
                  >
                    <Typography variant="subtitle2">{`${totalAssessments} MDS`}</Typography>
                  </Stack>
                )}
              </Stack>
              <Stack>
                <Tooltip
                  title={getTooltipMessage}
                  followCursor
                >
                  <span>
                    <Button
                      color="inherit"
                      startIcon={
                        <SvgIcon>
                          <Plus />
                        </SvgIcon>
                      }
                      sx={{ color: 'primary.dark' }}
                      disabled={!doesPatientHaveOpenEOC || !hasUpdatePatientPermission}
                      onClick={handleNewAssessment}
                    >
                      New Assessment
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </>
        )}

        {isLoading ? (
          <>
            {Array(12)
              .fill(null)
              .map((_, index) => (
                <Skeleton key={index} />
              ))}
          </>
        ) : (
          <MdsAssessmentTable
            mdsAssessments={allMdsAssessments}
            handleEditMds={handleEditMds}
            handleDeleteMds={handleDeleteMdsClick}
            showActions={true}
            onNewAssessmentClick={handleNewAssessment}
            doesPatientHaveOpenEOC={doesPatientHaveOpenEOC}
            hasUpdatePatientPermission={hasUpdatePatientPermission}
          />
        )}
      </Stack>

      <BootstrapDialog
        onClose={() => {
          setDeleteMdsAssessment(false);
          setDeleteMds(null);
        }}
        aria-labelledby="customized-dialog-title"
        open={deleteMdsAssessment}
      >
        <DeletePopupModal
          close={() => {
            setDeleteMdsAssessment(false);
            setDeleteMds(null);
          }}
          message="Are you sure you want to delete this MDS assessment? This action cannot be undone."
          button={{ saveLabel: 'Delete', closeLabel: 'Cancel' }}
          title="Delete MDS Assessment"
          handleSaveBtn={handleDeleteMds}
        />
      </BootstrapDialog>

      {isSnackBarOpen && message && (
        <SimpleSnackbar
          message={message}
          isError={false}
        />
      )}
    </Card>
  );
}

export default MdsAssessments;
